import dayjs from 'dayjs';

import React, { useState, useEffect, useContext } from 'react';
import Header from '../components/Header';
import { Box, Button, Container, Grid, Typography, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Collapse, Chip, Select, FormControl, InputLabel } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PreviewIcon from '@mui/icons-material/Preview';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';


import { findApplicableSections, saveGrouping, deleteSection, findSectionsForFilter } from '../../actions/uiActions';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { CompanyContext } from '../../CompanyProvider';

const TYPE_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'ITEMS', label: 'Items' },
  { value: 'GRP', label: 'Group' }
];

const GROUPING_STATUS_OPTIONS = [
  { value: 'Valid', label: 'Valid' },
  { value: 'Expired', label: 'Expired' }
];


const PAYMENT_MODES = [
  { value: 'CASH', label: 'CASH' },
  { value: 'POINT', label: 'POINT' },
  { value: 'COUPON', label: 'COUPON' }
];
const DISPLAY_MODE = [
  { value: 'A', label: 'Display to All' },
  { value: 'S', label: 'Restricted' }
];

// Helper to format date as yyyy-MM-dd using dayjs
function formatDate(d) {
  if (!d) return '';
  const dateObj = dayjs(d);
  if (!dateObj.isValid()) return '';
  return dateObj.format('YYYY-MM-DD');
}
function GroupingsPage({handleLogout}) {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [type, setType] = useState('ALL');
  const [groupingStatus, setGroupingStatus] = useState('Valid'); // New state for status
  const [groupings, setGroupings] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('ITEMS');
  const [isEditMode, setIsEditMode] = useState(false);
  const [newGroup, setNewGroup] = useState({
    sectionID: '',
    sectionName: '',
    validFrom: null,
    validTo: null,
    type: 'ITEMS',
    image: null,
    imagePreview: null,
    content: '',
    paymentMode: '',
    displayMode: 'A',
    sectionsDetails: [],
  });
  const [newDetail, setNewDetail] = useState({
    sectionDetailsID: '',
    sectionDetailsName: '',
    content: '',
    image: null,
    imagePreview: null,
  });
  const [previewImg, setPreviewImg] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [expanded, setExpanded] = useState({});
  const companyCode = useContext(CompanyContext);
  useEffect(() => {
    const fetchSections = async () => {
      try {
        const data = await findApplicableSections(companyCode);
        setGroupings(data || []);
      } catch (err) {
        setGroupings([]);
      }
    };
    fetchSections();
  }, []);

  const handleExpandClick = (sectionID) => {
    setExpanded((prev) => ({ ...prev, [sectionID]: !prev[sectionID] }));
  };

  // Fetch groupings (optionally with filters)
  const fetchGroupings = async () => {
    try {
      // Pass groupingStatus to your backend filter function
      const data = await findApplicableSections(companyCode, groupingStatus);
      setGroupings(data || []);
    } catch (err) {
      setGroupings([]);
    }
  };
  // Update handleList to call findSectionsForFilter with groupingStatus
  const handleList = async () => {
    try {
      const data = await findSectionsForFilter(companyCode, groupingStatus);
      setGroupings(data || []);
    } catch (err) {
      setGroupings([]);
    }
  };
  const handleCreateNew = () => {
    setOpenDialog(true);
    setDialogType('ITEMS');
    setIsEditMode(false);
    setNewGroup({
      sectionID: '',
      sectionName: '',
      validFrom: dayjs().subtract(3, 'day'),
      validTo: dayjs().add(6, 'month'),
      type: 'ITEMS',
      image: null,
      imagePreview: null,
      content: '',
      paymentMode: 'CASH',
      displayMode: 'A',
      sectionsDetails: [],
    });
    setNewDetail({
      sectionDetailsID: '',
      sectionDetailsName: '',
      content: '',
      image: null,
      imagePreview: null,
    });
  };

  const handleEditGrouping = (grouping) => {
    setOpenDialog(true);
    setIsEditMode(true);
    setDialogType(grouping.type);
    setNewGroup({
      sectionID: grouping.sectionID,
      sectionName: grouping.sectionName,
      validFrom: grouping.validFrom ? dayjs(grouping.validFrom) : null,
      validTo: grouping.validTo ? dayjs(grouping.validTo) : null,
      type: grouping.type,
      image: null,
      imagePreview: grouping.image || null,
      content: grouping.content || '',
      paymentMode: grouping.paymentMode || '',
      displayMode: grouping.displayMode || 'A',
      sectionsDetails: grouping.sectionsDetails || [],
    });
    setNewDetail({
      sectionDetailsID: '',
      sectionDetailsName: '',
      content: '',
      image: null,
      imagePreview: null,
    });
  };

  const handleDialogTypeChange = (e) => {
    setDialogType(e.target.value);
    setNewGroup({ ...newGroup, type: e.target.value, content: '', paymentMode: '', displayMode: 'A', sectionsDetails: [] });
  };

  const handleGroupImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewGroup({ ...newGroup, image: file, imagePreview: URL.createObjectURL(file) });
    }
  };

  const handleDetailImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewDetail({ ...newDetail, image: file, imagePreview: URL.createObjectURL(file) });
    }
  };

  const handleAddDetail = () => {
    // Prevent adding if required fields are empty
    if (!newDetail.sectionDetailsID || !newDetail.sectionDetailsName) return;
    // Shallow copy to avoid reference issues
    const detailToAdd = { ...newDetail };
    setNewGroup(prev => ({
      ...prev,
      sectionsDetails: [...(prev.sectionsDetails || []), detailToAdd],
    }));
    setNewDetail({ sectionDetailsID: '', sectionDetailsName: '', content: '', image: null, imagePreview: null });
  };

  const handleRemoveDetail = (idx) => {
    setNewGroup({
      ...newGroup,
      sectionsDetails: newGroup.sectionsDetails.filter((_, i) => i !== idx),
    });
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleDialogSave = async () => {
    // Validate required fields
    if (!newGroup.sectionID || !newGroup.sectionName || !newGroup.validFrom || !newGroup.validTo) {
      setSnackbar({ open: true, message: 'Please fill in all required fields (Section ID, Section Name, Valid From, Valid To)', severity: 'error' });
      return;
    }
    
    // Validate content for ITEMS type
    if (dialogType === 'ITEMS' && !newGroup.content) {
      setSnackbar({ open: true, message: 'Please specify applicable SKUs for Items type', severity: 'error' });
      return;
    }

    // Helper to format date to yyyy-MM-dd
    const toDateString = d => {
      if (!d) return null;
      if (typeof d === 'string') return d;
      if (d.format) return d.format('YYYY-MM-DD'); // dayjs
      if (d instanceof Date) return d.toISOString().slice(0, 10);
      return String(d);
    };
    // Prepare section detail images array (only files, in order)
    let sectionDetailImagesFiles = [];
    if (Array.isArray(newGroup.sectionsDetails)) {
      sectionDetailImagesFiles = newGroup.sectionsDetails.map(det => det.image || null);
    }
    try {
      await saveGrouping(
        companyCode,
        {
          ...newGroup,
          validFrom: toDateString(newGroup.validFrom),
          validTo: toDateString(newGroup.validTo),
          imagePreview: undefined,
          sectionsDetails: newGroup.sectionsDetails
            ? newGroup.sectionsDetails.map(({ imagePreview, ...rest }) => rest)
            : [],
        },
        newGroup.image,
        sectionDetailImagesFiles
      );
      setSnackbar({ open: true, message: 'Grouping saved successfully!', severity: 'success' });
      setOpenDialog(false);
      handleList();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save grouping', severity: 'error' });
    }
  };
  // Delete a grouping/section
  const handleDeleteSection = async (sectionID) => {
    try {
      await deleteSection(companyCode, sectionID);
      setSnackbar({ open: true, message: 'Grouping deleted successfully!', severity: 'success' });
      fetchGroupings();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete grouping', severity: 'error' });
    }
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // Helper for previewing images (handles both file object and URL string)
  const handlePreview = (img) => {
    if (!img) return;
    if (typeof img === 'string') {
      setPreviewImg(img);
      setOpenPreview(true);
    } else if (img instanceof File) {
      setPreviewImg(URL.createObjectURL(img));
      setOpenPreview(true);
    }
  };


  return (
    <Box sx={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Header handleLogout={handleLogout}/>
      <Container
        sx={{
          pt: 4,
          pb: 4,
          mt: 2,
          background: '#ffffff',
          border: '1px solid #e8edf5',
          borderRadius: 4,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.04)',
          minWidth: "95%",
          minHeight: '80vh',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a3c6e', mb: 1.5, mt: 0, letterSpacing: 0.5, fontSize: 24, textAlign: 'left' }}>
          Groupings
        </Typography>
        <Divider sx={{ mb: 2, borderColor: '#e2e8f0' }} />
        {/* Filter Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4} md={3} sx={{width:"20%"}}>
            <FormControl fullWidth size="small">
              <InputLabel id="grouping-status-label">Grouping Status</InputLabel>
              <Select
                labelId="grouping-status-label"
                id="grouping-status"
                value={groupingStatus}
                label="Grouping Status"
                onChange={e => setGroupingStatus(e.target.value)}
              >
                {GROUPING_STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button variant="contained" color="primary" sx={{ minWidth: 100, background: '#1a3c6e', color: '#fff', fontWeight: 700, borderRadius: 2, boxShadow: '0 2px 8px 0 rgba(26,60,110,0.13)' }} onClick={handleList}>
              List
            </Button>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button variant="outlined" color="primary" sx={{ minWidth: 140, borderColor: '#1a3c6e', color: '#1a3c6e', fontWeight: 700, borderRadius: 2, background: 'rgba(26,60,110,0.07)' }} onClick={handleCreateNew}>
              + New Grouping
            </Button>
          </Grid>
        </Grid>
        {/* New Grouping Dialog */}
        <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', fontSize: 18, background: '#ffffff', borderBottom: '1px solid #e8edf5' }}>
            {isEditMode ? 'Edit Grouping' : 'Add New Grouping'}
          </DialogTitle>
          <DialogContent dividers sx={{ background: '#ffffff', pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} style={{width:"30%"}}>
                <TextField 
                  label="Section/Group ID" 
                  value={newGroup.sectionID} 
                  onChange={e => setNewGroup({ ...newGroup, sectionID: e.target.value })} 
                  fullWidth 
                  required
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} style={{width:"68%"}}>
                <TextField 
                  label="Section/Group Name" 
                  value={newGroup.sectionName} 
                  onChange={e => setNewGroup({ ...newGroup, sectionName: e.target.value })} 
                  fullWidth 
                  required
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} style={{width:"30%"}}>
                <DatePicker 
                  label="Valid From" 
                  value={newGroup.validFrom} 
                  onChange={val => setNewGroup({ ...newGroup, validFrom: val })} 
                  slotProps={{ textField: { fullWidth: true, required: true, InputProps: { sx: { background: '#f8fafc', borderRadius: 2 } } } }} 
                />
              </Grid>
              <Grid item xs={12} sm={6} style={{width:"30%"}}>
                <DatePicker 
                  label="Valid To" 
                  value={newGroup.validTo} 
                  onChange={val => setNewGroup({ ...newGroup, validTo: val })} 
                  slotProps={{ textField: { fullWidth: true, required: true, InputProps: { sx: { background: '#f8fafc', borderRadius: 2 } } } }} 
                />
              </Grid>
              <Grid item xs={12} sm={6} style={{width:"36%"}}>
                <TextField 
                  select 
                  label="Type" 
                  value={dialogType} 
                  onChange={handleDialogTypeChange} 
                  fullWidth 
                  required
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { background: '#ffffff' } } } }}
                >
                  <MenuItem value="ITEMS" sx={{ color: '#1a3c6e', fontWeight: 500 }}>Items/SKUs</MenuItem>
                  <MenuItem value="GRP" sx={{ color: '#1a3c6e', fontWeight: 500 }}>Group</MenuItem>
                </TextField>
              </Grid>
              {dialogType === 'ITEMS' &&<Grid item xs={12} sm={6} style={{width:"30%"}}>
                <FormControl fullWidth required>
                  <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
                  <Select
                    labelId="payment-mode-label"
                    id="payment-mode"
                    multiple
                    label="Payment Mode"
                    value={newGroup.paymentMode ? newGroup.paymentMode.split(',') : []}
                    onChange={e => setNewGroup({ ...newGroup, paymentMode: e.target.value.join(',') })}
                    sx={{ background: '#f8fafc', borderRadius: 2 }}
                    MenuProps={{ PaperProps: { sx: { background: '#ffffff' } } }}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {PAYMENT_MODES.map((mode) => (
                      <MenuItem key={mode.value} value={mode.value} sx={{ color: '#1a3c6e', fontWeight: 500 }}>
                        {mode.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>}
              {dialogType === 'ITEMS' && <Grid item xs={12} sm={6} style={{width:"30%"}}>
                <TextField 
                  select 
                  label="Display Mode" 
                  value={newGroup.displayMode} 
                  onChange={e => setNewGroup({ ...newGroup, displayMode: e.target.value })} 
                  fullWidth 
                  required
                  InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { background: '#ffffff' } } } }}
                >
                  {DISPLAY_MODE.map((mode) => (
                    <MenuItem key={mode.value} value={mode.value} sx={{ color: '#1a3c6e', fontWeight: 500 }}>{mode.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>}

              {dialogType === 'ITEMS' && (
                <Grid item xs={12} style={{width:"100%"}}>
                  <TextField 
                    label="Specify applicable SKUs" 
                    value={newGroup.content} 
                    onChange={e => setNewGroup({ ...newGroup, content: e.target.value })} 
                    fullWidth
                    required
                    InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} style={{width:"100%"}}>
                <Box sx={{ border: '2px dashed #cbd5e1', borderRadius: 3, background: '#f8fafc', p: 2, textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => document.getElementById('group-image-upload').click()}>
                  <input id="group-image-upload" type="file" accept="image/*" hidden onChange={handleGroupImageChange} />
                  <Typography sx={{ color: '#1a3c6e', fontWeight: 500 }}>Image</Typography>
                  <Button variant="contained" sx={{ background: '#1a3c6e', color: '#fff', borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 700, mt: 1 }} onClick={e => { e.stopPropagation(); document.getElementById('group-image-upload').click(); }}>Upload</Button>
                  {newGroup.imagePreview && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <img src={newGroup.imagePreview} alt="preview" style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8, border: '2px solid #1a3c6e' }} />
                    </Box>
                  )}
                </Box>
              </Grid>
              
              {dialogType === 'GRP' && (
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: 700, color: '#1a3c6e', mb: 1 }}>Group Details</Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <TextField 
                        label="Section Detail ID" 
                        value={newDetail.sectionDetailsID} 
                        onChange={e => setNewDetail({ ...newDetail, sectionDetailsID: e.target.value })} 
                        fullWidth
                        InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField 
                        label="Section Detail Name" 
                        value={newDetail.sectionDetailsName} 
                        onChange={e => setNewDetail({ ...newDetail, sectionDetailsName: e.target.value })} 
                        fullWidth
                        InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField 
                        label="Content" 
                        value={newDetail.content} 
                        onChange={e => setNewDetail({ ...newDetail, content: e.target.value })} 
                        fullWidth
                        InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Box sx={{ border: '1px dashed #cbd5e1', borderRadius: 2, background: '#f8fafc', p: 1, textAlign: 'center', cursor: 'pointer' }}
                        onClick={() => document.getElementById('detail-image-upload').click()}>
                        <input id="detail-image-upload" type="file" accept="image/*" hidden onChange={handleDetailImageChange} />
                        <Button variant="outlined" sx={{ color: '#1a3c6e', borderColor: '#1a3c6e', borderRadius: 2, px: 2, textTransform: 'none', fontWeight: 700 }} onClick={e => { e.stopPropagation(); document.getElementById('detail-image-upload').click(); }}>Upload</Button>
                        {newDetail.imagePreview && (
                          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <img src={newDetail.imagePreview} alt="preview" style={{ maxWidth: 60, maxHeight: 60, borderRadius: 6, border: '2px solid #1a3c6e' }} />
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton color="primary" sx={{ p: 0.5 }} onClick={handleAddDetail} aria-label="Add Detail">
                        <AddCircleOutlineIcon sx={{ color: '#1a3c6e', fontSize: 28 }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                  {/* List of added details */}
                  {newGroup.sectionsDetails.length > 0 && (
                    <Table size="small" sx={{ mt: 2,width:"100%" }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Section Detail ID</TableCell>
                          <TableCell>Section Detail Name</TableCell>
                          <TableCell>Content</TableCell>
                          <TableCell>Image</TableCell>
                          <TableCell>Remove</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {newGroup.sectionsDetails.map((det, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{det.sectionDetailsID}</TableCell>
                            <TableCell>{det.sectionDetailsName}</TableCell>
                            <TableCell>{det.content}</TableCell>
                            <TableCell>
                              {det.imagePreview && (
                                <img src={det.imagePreview} alt="preview" style={{ maxWidth: 40, maxHeight: 40, borderRadius: 4, border: '2px solid #1a3c6e' }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton color="error" size="small" onClick={() => handleRemoveDetail(idx)} aria-label="Delete Detail">
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ background: '#ffffff', borderTop: '1px solid #e8edf5', pb: 2, pt: 2 }}>
            <Button onClick={handleDialogClose} color="secondary" sx={{ color: '#1a3c6e', borderColor: '#1a3c6e', fontWeight: 700, borderRadius: 2, px: 3, textTransform: 'none', background: 'rgba(26,60,110,0.07)' }}>Cancel</Button>
            <Button onClick={handleDialogSave} color="primary" variant="contained" sx={{ background: '#1a3c6e', color: '#fff', fontWeight: 700, borderRadius: 2, px: 3, textTransform: 'none', boxShadow: '0 2px 8px 0 rgba(26,60,110,0.13)' }}>Save</Button>
          </DialogActions>
        </Dialog>
        {/* Details Section - Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, background: '#ffffff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e8edf5', width: '100%', mx: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Section ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Section Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Valid From</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Valid To</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Content</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Helper to format date as yyyy-MM-dd */}
              {groupings.map((grp, idx) => (
                <React.Fragment key={grp.sectionID}>
                  <TableRow>
                    <TableCell>
                      {grp.type === 'GRP' && (grp.sectionsDetails && grp.sectionsDetails.length > 0) ? (
                        <IconButton size="small" onClick={() => handleExpandClick(grp.sectionID)}>
                          {expanded[grp.sectionID] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      ) : null}
                      {grp.sectionID}
                    </TableCell>
                    <TableCell>{grp.sectionName}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handlePreview(grp.image)} size="small">
                        <PreviewIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>{formatDate(grp.validFrom)}</TableCell>
                    <TableCell>{formatDate(grp.validTo)}</TableCell>
                    <TableCell>
                      <Chip
                        label={grp.type === 'GRP' ? 'Group' : grp.type === 'ITEMS' ? 'Items' : grp.type}
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          borderRadius: 2,
                          fontSize: 13,
                          px: 1.5,
                          color: grp.type === 'GRP' ? '#1a3c6e' : grp.type === 'ITEMS' ? '#1a3c6e' : '#888',
                          borderColor: grp.type === 'GRP' ? '#1a3c6e' : grp.type === 'ITEMS' ? '#1a3c6e' : '#888',
                          background: '#f0f4ff',
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={grp.content} arrow>
                        <span style={{ cursor: 'pointer', borderBottom: '1px dotted #1a3c6e', color: '#1a3c6e' }}>
                          {grp.content && grp.content.length > 20 ? grp.content.slice(0, 20) + '...' : grp.content}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" sx={{ color: '#1a3c6e' }} onClick={() => handleEditGrouping(grp)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: '#d32f2f' }} onClick={() => handleDeleteSection(grp.sectionID)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                {/* If type is GRP, show details table as collapsible */}
                {grp.type === 'GRP' && Array.isArray(grp.sectionsDetails) && grp.sectionsDetails.length > 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ background: '#f8fafc', p: 0, border: 0 }}>
                      <Collapse in={expanded[grp.sectionID]} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a3c6e', mb: 1 }}>
                            Group Details
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Section Detail Name</TableCell>
                                <TableCell>Image</TableCell>
                                <TableCell>Content</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {grp.sectionsDetails.map((det) => (
                                <TableRow key={det.sectionDetailsID}>
                                  <TableCell>{det.sectionDetailsName}</TableCell>
                                  <TableCell>
                                    <IconButton onClick={() => { setPreviewImg(det.image); setOpenPreview(true); }} size="small">
                                      <PreviewIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={det.content} arrow>
                                      <span style={{ cursor: 'pointer', borderBottom: '1px dotted #1a3c6e', color: '#1a3c6e' }}>
                                        {det.content && det.content.length > 100 ? det.content.slice(0, 100) + '...' : det.content}
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
              ))}
        </TableBody>
            </Table>
          </TableContainer>
          {/* Image Preview Dialog */}
          <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="xs">
            <DialogTitle sx={{ color: '#0f172a', fontWeight: 700, background: '#ffffff', borderBottom: '1px solid #e8edf5' }}>Image Preview</DialogTitle>
            <DialogContent sx={{ background: '#ffffff' }}>
              <img src={previewImg} alt="preview" style={{ maxWidth: 350, maxHeight: 350, borderRadius: 8, border: '2px solid #1a3c6e' }} />
            </DialogContent>
            <DialogActions sx={{ background: '#ffffff' }}>
              <Button onClick={() => setOpenPreview(false)} color="primary" sx={{ color: '#1a3c6e', fontWeight: 700 }}>Close</Button>
            </DialogActions>
          </Dialog>
        {/* Snackbar for feedback */}
        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%', fontWeight: 500 }}>
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
        </Container>
      </LocalizationProvider>
    </Box>
  );
}

export default GroupingsPage;
